/**
 * License Service Factory
 * Creates and configures all license validation components
 */

import { GumroadLicenseValidator } from './GumroadLicenseValidator';
import { BetaLicenseValidator } from './BetaLicenseValidator';
import { CustomLicenseValidator } from './CustomLicenseValidator';
import { LicenseValidationChain } from './LicenseValidationChain';
import { SecureLicenseStorageImpl } from './SecureLicenseStorage';
import { DeviceLimitManagerImpl } from './DeviceLimitManager';
import { OfflineGracePeriodManagerImpl } from './OfflineGracePeriodManager';
import { LicenseActivationFlowImpl } from './LicenseActivationFlow';

import type {
  LicenseValidator,
  SecureLicenseStorage,
  DeviceLimitManager,
  OfflineGracePeriodManager,
  LicenseActivationFlow,
  LicenseValidatorConfig
} from './interfaces';

/**
 * Complete license service suite
 */
export interface LicenseServiceSuite {
  validator: LicenseValidator;
  storage: SecureLicenseStorage;
  deviceManager: DeviceLimitManager;
  gracePeriodManager: OfflineGracePeriodManager;
  activationFlow: LicenseActivationFlow;
}

/**
 * Factory configuration
 */
export interface LicenseServiceFactoryConfig {
  validator?: Partial<LicenseValidatorConfig>;
  enableDeviceFingerprinting?: boolean;
  enableOfflineGracePeriod?: boolean;
  enableSecureStorage?: boolean;
}

export class LicenseServiceFactory {
  private static instance: LicenseServiceSuite | null = null;

  /**
   * Create complete license service suite
   */
  static createLicenseServices(config: LicenseServiceFactoryConfig = {}): LicenseServiceSuite {
    // Return singleton instance if already created
    if (this.instance) {
      return this.instance;
    }

    const {
      validator: validatorConfig = {},
      enableDeviceFingerprinting = true,
      enableOfflineGracePeriod = true,
      enableSecureStorage = true
    } = config;

    try {
      // Create core services
      const storage = enableSecureStorage 
        ? new SecureLicenseStorageImpl()
        : new MockSecureLicenseStorage();

      const deviceManager = enableDeviceFingerprinting
        ? new DeviceLimitManagerImpl()
        : new MockDeviceLimitManager();

      const gracePeriodManager = enableOfflineGracePeriod
        ? new OfflineGracePeriodManagerImpl()
        : new MockOfflineGracePeriodManager();

      // Create individual validators
      const betaValidator = new BetaLicenseValidator(
        storage,
        deviceManager,
        gracePeriodManager
      );

      const customValidator = new CustomLicenseValidator(
        storage,
        deviceManager,
        gracePeriodManager
      );

      const gumroadValidator = new GumroadLicenseValidator(
        storage,
        deviceManager,
        gracePeriodManager,
        validatorConfig
      );

      // Create validation chain (Beta → Custom → Gumroad)
      const validator = new LicenseValidationChain(
        betaValidator,
        customValidator,
        gumroadValidator
      );

      // Create activation flow
      const activationFlow = new LicenseActivationFlowImpl(
        validator,
        storage,
        deviceManager,
        gracePeriodManager
      );

      this.instance = {
        validator,
        storage,
        deviceManager,
        gracePeriodManager,
        activationFlow
      };

      return this.instance;
    } catch (error) {
      console.error('Failed to create license services:', error);
      
      // Return mock services as fallback
      return this.createMockServices();
    }
  }

  /**
   * Reset singleton instance (for testing)
   */
  static resetInstance(): void {
    this.instance = null;
  }

  /**
   * Create mock services for testing or fallback
   */
  private static createMockServices(): LicenseServiceSuite {
    const storage = new MockSecureLicenseStorage();
    const deviceManager = new MockDeviceLimitManager();
    const gracePeriodManager = new MockOfflineGracePeriodManager();

    // Create individual validators
    const betaValidator = new BetaLicenseValidator(
      storage,
      deviceManager,
      gracePeriodManager
    );

    const customValidator = new CustomLicenseValidator(
      storage,
      deviceManager,
      gracePeriodManager
    );

    const gumroadValidator = new GumroadLicenseValidator(
      storage,
      deviceManager,
      gracePeriodManager
    );

    // Create validation chain
    const validator = new LicenseValidationChain(
      betaValidator,
      customValidator,
      gumroadValidator
    );

    const activationFlow = new LicenseActivationFlowImpl(
      validator,
      storage,
      deviceManager,
      gracePeriodManager
    );

    return {
      validator,
      storage,
      deviceManager,
      gracePeriodManager,
      activationFlow
    };
  }
}

/**
 * Mock implementations for fallback or testing
 */

import type { LicenseInfo, DeviceInfo, OfflineGracePeriodStatus } from '$lib/types/subscription';

class MockSecureLicenseStorage implements SecureLicenseStorage {
  private storage = new Map<string, LicenseInfo>();

  async storeLicense(licenseKey: string, licenseInfo: LicenseInfo): Promise<void> {
    this.storage.set(licenseKey, licenseInfo);
  }

  async getLicense(licenseKey: string): Promise<LicenseInfo | null> {
    return this.storage.get(licenseKey) || null;
  }

  async removeLicense(licenseKey: string): Promise<void> {
    this.storage.delete(licenseKey);
  }

  async hasLicense(licenseKey: string): Promise<boolean> {
    return this.storage.has(licenseKey);
  }

  async listLicenseKeys(): Promise<string[]> {
    return Array.from(this.storage.keys());
  }

  async clearAllLicenses(): Promise<void> {
    this.storage.clear();
  }

  async verifyIntegrity(): Promise<boolean> {
    return true;
  }
}

class MockDeviceLimitManager implements DeviceLimitManager {
  private devices = new Map<string, DeviceInfo[]>();
  
  async getDeviceCount(licenseKey: string): Promise<number> {
    return this.devices.get(licenseKey)?.length || 0;
  }

  async getActiveDevices(licenseKey: string): Promise<DeviceInfo[]> {
    return this.devices.get(licenseKey) || [];
  }

  async addDevice(licenseKey: string, deviceInfo: DeviceInfo): Promise<boolean> {
    const existing = this.devices.get(licenseKey) || [];
    existing.push(deviceInfo);
    this.devices.set(licenseKey, existing);
    return true;
  }

  async removeDevice(licenseKey: string, deviceId: string): Promise<boolean> {
    const existing = this.devices.get(licenseKey) || [];
    const filtered = existing.filter(d => d.deviceId !== deviceId);
    this.devices.set(licenseKey, filtered);
    return filtered.length < existing.length;
  }

  async canAddDevice(licenseKey: string, maxDevices: number): Promise<boolean> {
    const count = await this.getDeviceCount(licenseKey);
    return count < maxDevices;
  }

  async updateDeviceActivity(licenseKey: string, deviceId: string): Promise<void> {
    // Mock implementation
  }

  async generateDeviceFingerprint(): Promise<string> {
    return 'mock-fingerprint-' + Date.now();
  }

  async getCurrentDeviceInfo(): Promise<DeviceInfo> {
    return {
      deviceId: await this.generateDeviceFingerprint(),
      deviceName: 'Mock Device',
      platform: 'Mock Platform',
      activatedAt: new Date(),
      lastActiveAt: new Date(),
      isCurrentDevice: true
    };
  }
}

class MockOfflineGracePeriodManager implements OfflineGracePeriodManager {
  async startGracePeriod(licenseKey: string): Promise<void> {
    // Mock implementation
  }

  async isInGracePeriod(licenseKey: string): Promise<boolean> {
    return true; // Always allow offline validation in mock
  }

  async getGracePeriodStatus(licenseKey: string): Promise<OfflineGracePeriodStatus> {
    return {
      isInGracePeriod: true,
      lastValidationAt: new Date(),
      gracePeriodExpiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      daysRemaining: 7
    };
  }

  async endGracePeriod(licenseKey: string): Promise<void> {
    // Mock implementation
  }

  async extendGracePeriod(licenseKey: string, additionalHours: number): Promise<void> {
    // Mock implementation
  }

  async cleanExpiredGracePeriods(): Promise<void> {
    // Mock implementation
  }
}